import React, { useState, useEffect } from 'react';
import {
  DollarSign, ShoppingBag, TrendingUp,
  Package, AlertTriangle, Calendar, Store, Truck, CreditCard
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import { reportApi, productApi } from '../api';

// 各维度的显示标签+颜色，写在组件外面避免每次渲染重新创建
const DELIVERY_TYPE_META = {
  instant:   { label: '立即送', color: 'bg-green-500' },
  scheduled: { label: '预约送', color: 'bg-blue-500' },
  pickup:    { label: '自提',   color: 'bg-orange-500' }
};
const PAYMENT_METHOD_META = {
  khqr:   { label: 'KHQR',   color: 'bg-indigo-500' },
  alipay: { label: '支付寶', color: 'bg-sky-500' },
  usdt:   { label: 'USDT',   color: 'bg-emerald-500' },
  cod:    { label: '货到付款', color: 'bg-gray-400' }
};

const DashboardPage = () => {
  const { language, currentUser } = useApp();
  const t = translations[language];

  const [todayStats, setTodayStats] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  // 新增：配送方式占比 / 支付方式占比 / 运费概览
  const [orderInsights, setOrderInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadDashboardData(); }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [todayRes, topRes, lowStockRes, insightsRes] = await Promise.all([
        reportApi.getTodayOverview(),
        reportApi.getTopProducts({ limit: 5 }),
        productApi.getLowStock(),
        // 新增接口，后端 reportController.js 还没实现，接口没接好之前这里会请求失败，
        // 已经用 try/catch 包住了，不会导致整个看板加载失败，只是这块卡片先不显示数据
        reportApi.getOrderInsights(),
      ]);
      if (todayRes.success)    setTodayStats(todayRes.data);
      if (topRes.success)      setTopProducts(topRes.data || []);
      if (lowStockRes.success) setLowStockProducts(lowStockRes.data || []);
      if (insightsRes.success) setOrderInsights(insightsRes.data);
    } catch (err) {
      console.error('加载数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const getProductName = (product) => {
    if (language === 'en') return product.name_en || product.product_name || product.name;
    if (language === 'km') return product.name_km || product.product_name || product.name;
    return product.product_name || product.name;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">{language === 'en' ? 'Loading...' : '加载中...'}</p>
        </div>
      </div>
    );
  }

  const isHQ = currentUser?.store_type === 'headquarters';

  // 把 { instant: 12, scheduled: 8, pickup: 5 } 这种计数对象，
  // 转成带百分比、排好序的数组，方便渲染横条
  const buildBreakdown = (counts, metaMap) => {
    if (!counts) return [];
    const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
    if (total === 0) return [];
    return Object.entries(counts)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => ({
        key,
        count,
        percent: Math.round((count / total) * 100),
        label: metaMap[key]?.label || key,
        color: metaMap[key]?.color || 'bg-gray-400'
      }))
      .sort((a, b) => b.count - a.count);
  };

  const deliveryBreakdown = buildBreakdown(orderInsights?.delivery_type_counts, DELIVERY_TYPE_META);
  const paymentBreakdown = buildBreakdown(orderInsights?.payment_method_counts, PAYMENT_METHOD_META);

  return (
    <div className="p-6 space-y-6">
      {/* 头部 */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">
            {language === 'en' ? 'Dashboard' : language === 'km' ? 'ផ្ទាំងគ្រប់គ្រង' : '数据看板'}
          </h2>
          <p className="text-gray-600 mt-1">
            {language === 'en' ? 'Overview of your business performance' : '查看您的业务表现概览'}
          </p>
        </div>
        <div className="flex items-center space-x-2 text-gray-600">
          <Calendar className="w-5 h-5" />
          <span>{new Date().toLocaleDateString(language === 'km' ? 'km-KH' : language, {
            year: 'numeric', month: 'long', day: 'numeric'
          })}</span>
        </div>
      </div>

      {/* 今日统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            label: language === 'en' ? 'Today Sales' : '今日销售额',
            value: `$${parseFloat(todayStats?.today_sales || 0).toFixed(2)}`,
            sub: language === 'en' ? 'Revenue' : '营业收入',
            icon: DollarSign, color: 'green'
          },
          {
            label: language === 'en' ? 'Transactions' : '交易数量',
            value: todayStats?.today_orders || 0,
            sub: language === 'en' ? 'Orders completed' : '已完成订单',
            icon: ShoppingBag, color: 'blue'
          },
          {
            label: language === 'en' ? 'Avg. Transaction' : '平均客单价',
            value: `$${todayStats?.today_orders > 0
              ? (parseFloat(todayStats.today_sales) / todayStats.today_orders).toFixed(2)
              : '0.00'}`,
            sub: language === 'en' ? 'Per order' : '每笔订单',
            icon: TrendingUp, color: 'purple'
          },
          {
            label: language === 'en' ? 'Low Stock' : '低库存预警',
            value: todayStats?.low_stock_products || 0,
            sub: language === 'en' ? 'Products' : '种商品',
            icon: Package, color: 'orange'
          },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{label}</p>
                <p className="text-3xl font-bold mt-2">{value}</p>
              </div>
              <div className={`bg-${color}-100 p-3 rounded-full`}>
                <Icon className={`w-8 h-8 text-${color}-600`} />
              </div>
            </div>
            <div className={`mt-4 flex items-center text-sm text-${color}-600`}>
              <TrendingUp className="w-4 h-4 mr-1" />
              <span>{sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* 总店：分店销售汇总 */}
      {isHQ && todayStats?.store_summary?.length > 0 && (
        <div className="bg-white rounded-xl shadow">
          <div className="p-6 border-b flex items-center gap-2">
            <Store className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold">{language === 'en' ? 'Store Summary' : '各门店今日销售'}</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {todayStats.store_summary.map(store => (
                <div key={store.store_id} className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-gray-700">{store.store_name}</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    ${parseFloat(store.sales || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {store.orders || 0} {language === 'en' ? 'orders' : '笔订单'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 新增：配送方式占比 / 支付方式占比 / 运费概览 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 配送方式占比 */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-6 border-b flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold">配送方式占比</h3>
          </div>
          <div className="p-6">
            {deliveryBreakdown.length > 0 ? (
              <div className="space-y-4">
                {deliveryBreakdown.map(item => (
                  <div key={item.key}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-gray-700 font-medium">{item.label}</span>
                      <span className="text-gray-500">{item.count} 单 · {item.percent}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className={`${item.color} h-2 rounded-full`} style={{ width: `${item.percent}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>暂无数据</p>
              </div>
            )}
          </div>
        </div>

        {/* 支付方式占比 */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-6 border-b flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold">支付方式占比</h3>
          </div>
          <div className="p-6">
            {paymentBreakdown.length > 0 ? (
              <div className="space-y-4">
                {paymentBreakdown.map(item => (
                  <div key={item.key}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-gray-700 font-medium">{item.label}</span>
                      <span className="text-gray-500">{item.count} 单 · {item.percent}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className={`${item.color} h-2 rounded-full`} style={{ width: `${item.percent}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>暂无数据</p>
              </div>
            )}
          </div>
        </div>

        {/* 运费概览 */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-6 border-b flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold">运费概览</h3>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <p className="text-sm text-gray-500">运费总收入</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                ${parseFloat(orderInsights?.total_delivery_fee_collected || 0).toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">享受免运费的外送订单</p>
              <p className="text-2xl font-bold text-gray-800 mt-1">
                {orderInsights?.free_shipping_order_count || 0}
                <span className="text-sm text-gray-400 font-normal ml-1">
                  / {orderInsights?.total_delivery_order_count || 0} 单外送
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 畅销商品 */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-6 border-b">
            <h3 className="text-lg font-bold">
              {language === 'en' ? 'Top Selling Products' : '畅销商品'}
            </h3>
          </div>
          <div className="p-6">
            {topProducts.length > 0 ? (
              <div className="space-y-4">
                {topProducts.map((product, index) => (
                  <div key={product.product_id || index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                        index === 0 ? 'bg-yellow-100 text-yellow-600' :
                        index === 1 ? 'bg-gray-100 text-gray-600' :
                        index === 2 ? 'bg-orange-100 text-orange-600' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium">{getProductName(product)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">
                        {product.total_quantity || 0} {language === 'en' ? 'sold' : '已售'}
                      </p>
                      <p className="text-sm text-gray-500">
                        ${parseFloat(product.total_sales || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{language === 'en' ? 'No data' : '暂无数据'}</p>
              </div>
            )}
          </div>
        </div>

        {/* 低库存预警 */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-6 border-b flex justify-between items-center">
            <h3 className="text-lg font-bold">
              {language === 'en' ? 'Low Stock Alert' : '低库存预警'}
            </h3>
            {lowStockProducts.length > 0 && (
              <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-medium">
                {lowStockProducts.length}
              </span>
            )}
          </div>
          <div className="p-6">
            {lowStockProducts.length > 0 ? (
              <div className="space-y-4">
                {lowStockProducts.slice(0, 5).map((product) => (
                  <div key={product.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <div>
                        <p className="font-medium">{getProductName(product)}</p>
                        <p className="text-sm text-gray-500">
                          {language === 'en' ? 'Min stock' : '最低库存'}: {product.min_stock || 0}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-red-600">{product.stock || 0}</p>
                      <p className="text-xs text-gray-500">{language === 'en' ? 'in stock' : '库存'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-green-600 font-medium">
                  {language === 'en' ? '✓ All products well stocked' : '✓ 所有商品库存充足'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;